class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary:not(.no-control-menu)').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button:not(.no-control-menu)').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button') || detailsElement.querySelector(':scope > summary'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        this.hasBackButton ? document.body.classList.add('open-submenu-mobile') : '';
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    if(this.dataset.id) {
      document.body.classList.add(`open-drawer-${this.dataset.id}`);
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if(this.dataset.id) {
      document.body.classList.remove(`open-drawer-${this.dataset.id}`);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    closeDetailsInAnimation(detailsElement);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.hasBackButton = true;
  }

  openMenuDrawer(summaryElement) {
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.header = this.header || document.querySelector('.section-header');
    this.headerContent = document.querySelector('.header');
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.headerContent.getBoundingClientRect().bottom)}px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class BackButtonMenu extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    this.button.addEventListener('click', this.onButtonClickHandler);
    this.headerDrawer = document.querySelector('header-drawer');
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.onButtonClickHandler);
  }

  onButtonClick() {
    const openingElements = this.headerDrawer.querySelectorAll('.menu-opening');
    if(openingElements.length == 1) {
      this.headerDrawer.querySelector('.header__icon').dispatchEvent(new Event('click'));
    } else {
      const lastElement = openingElements[openingElements.length - 1];
      lastElement.querySelector(':scope > div > div > .menu-drawer__close-button').dispatchEvent(new Event('click'));
    }
  }
}

customElements.define('back-button-menu', BackButtonMenu);

const animateObserver = new IntersectionObserver((entries, observer) => {
  entries.forEach(entry => {
    if(entry.isIntersecting) {
      entry.target.showImages();
      observer.unobserve(entry.target);
    }
  });
}, {threshold: 0.1});

const animateResizeObserver = new ResizeObserver((entries, observer) => {
  if(window.innerWidth >= 750) {
    entries.forEach(entry => {
      if(!entry.target.inited) {
        entry.target.createObserver();
        observer.unobserve(entry.target);
      }
    });
  }
});

class UseAnimate extends HTMLElement {
  constructor() {
    super();
    this.inited = false;
  }

  connectedCallback() {
    if(window.innerWidth >= 750) {
      this.createObserver();
    } else {
      animateResizeObserver.observe(this);
    }
  }

  createObserver() {
    this.inited = true;
    animateObserver.observe(this);
  }

  showImages() {
    this.classList.add('loaded');
  }
}

customElements.define('use-animate', UseAnimate);

class ToastMessageManager extends HTMLElement {
  constructor() {
    super();
  }

  pushMessage(content, type) {
    var toast = document.createElement('toast-message');
    toast.setContent(content, type);
    this.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('open');
    }, 300);
  }
}

customElements.define('toast-message-manager', ToastMessageManager);

class ToastMessage extends HTMLElement
{
  constructor() {
    super();
    this.waitToCloseTimeout = setTimeout(() => {
      this.close();
    }, 3300);
  }

  connectedCallback() {
    this.appendCloseButton();
  }

  setContent(str, type) {
    this.appendChild(document.createElement('div'));
    this.contentWrapper = this.querySelector('div');
    this.contentWrapper.classList.add('alert');
    this.contentWrapper.textContent = str;
    this.contentWrapper.classList.add(`alert--${type}`);
  }
 
  close() {
    this.classList.remove('open');
    setTimeout(() => {
      this.remove();
    }, 300);
  }

  appendCloseButton() {
    const button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.classList.add('toast__button', 'color-foreground');
    const icon = document.querySelector('.icon-close-sample').cloneNode(true);
    icon.classList.remove('hidden');
    button.appendChild(icon);
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    button.addEventListener('click', this.onButtonClickHandler);
    this.contentWrapper.appendChild(button);
  }

  onButtonClick() {
    if(this.waitToCloseTimeout) {
      clearTimeout(this.waitToCloseTimeout);
    }
    this.close();
  }
}

customElements.define('toast-message', ToastMessage);

function pushMessage(content, type) {
  document.querySelector('toast-message-manager').pushMessage(content, type);
}

function pushSuccessMessage(content) {
  pushMessage(content, 'success');
}

function pushErrorMessage(content) {
  pushMessage(content, 'error');
}
  // Get the modal
  var perfectModal = document.getElementById("perfectModal");
  var shippingModal = document.getElementById("shippingModal");
  var pricingModal = document.getElementById("pricingModal");
  
  // Get the button that opens the modal
  var btn = document.getElementById("myBtn");
  
  // Get the <span> element that closes the modal
  var perfectSpan = document.getElementById("perfectSpan");
  var shippingSpan = document.getElementById("shippingSpan");
  var pricingSpan = document.getElementById("pricingSpan");
  
  // When the user clicks the button, open the modal 
  function openPerfectModal() {
    perfectModal.style.display = "block";
    document.body.classList.add('scroll-lock');
  }

  // Function to open shipping modal
  function openShippingModal() {
    shippingModal.style.display = "block";
    document.body.classList.add('scroll-lock');
  }

  // Function to open pricing modal
  function openPricingModal() {
    pricingModal.style.display = "block";
    document.body.classList.add('scroll-lock');
  }

  // When the user clicks on <perfectSpan> (x), close the modal
    // Event handlers to close modals
    perfectSpan.onclick = function() {
      perfectModal.style.display = "none";
      document.body.classList.remove('scroll-lock');
    }

    shippingSpan.onclick = function() {
      shippingModal.style.display = "none";
      document.body.classList.remove('scroll-lock');
    }

    pricingSpan.onclick = function() {
      pricingModal.style.display = "none";
      document.body.classList.remove('scroll-lock');
    }
  
  // When the user clicks anywhere outside of the modal, close it
  window.onclick = function(event) {
    if (event.target == perfectModal) {
      perfectModal.style.display = "none";
    } else if (event.target == shippingModal) {
      shippingModal.style.display = "none";
    } else if (event.target == pricingModal) {
      pricingModal.style.display = "none";
    }
  }

  
//our custom
$(document).ready(function(){
  //   $('.color_var_slide img.custom-zoom').on('click', function(){
  //     console.log('clicked');
  //     var this_attr = $(this).attr('data-var-id');
  //     $(this).parents('a.parent_zoom_custom').siblings('a.product__modal-opener--image[data-var-id="'+this_attr+'"]').addClass('hhhhhh');
  //     $(this).parents('a.parent_zoom_custom').siblings('a.product__modal-opener--image[data-var-id="'+this_attr+'"]').trigger('click');
  //   });
  //   var images = document.querySelectorAll('.color_var_slide img.custom-zoom');
  
  // images.forEach(function(image) {
  //     image.addEventListener('click', function() {
  //         console.log('clicked');
  
  //         var parentLink = this.closest('a.parent_zoom_custom');
  //         var siblingLink = parentLink ? parentLink.previousElementSibling : null;
  
  //         if (siblingLink) {
  //             siblingLink.click();
  //         }
  //     });
  // });
    $('.product__media-list li:gt(0)').find('a.product__modal-opener--image').removeClass('product__modal-opener--image');
    $(document).on('click','fieldset.product-form__input ul li', function(){
      setTimeout(function() {
      $('.product__media-list li:lt(1)').find('a.product__modal-opener').addClass('product__modal-opener--image');
      $('.product__media-list li:gt(0)').find('a.product__modal-opener--image').removeClass('product__modal-opener--image');
        }, 1000);
    });
  document.querySelectorAll('.color_var_slide img.custom-zoom').forEach(function(img) {
      img.addEventListener('click', function() {
          //console.log('clicked');
          var thisAttr = this.getAttribute('data-var-id');
          
          var parentLink = this.closest('a.parent_zoom_custom');
          if (parentLink) {
              var siblings = parentLink.parentNode.querySelectorAll('a.product__modal-opener--image[data-var-id="' + thisAttr + '"]');
              
              siblings.forEach(function(sibling) {
                  sibling.classList.add('hhhhhh');
                  sibling.click();
              });
          }
      });
  });
  
  
    
    $('.mobile-view_icon').on('click',function(){
      $(this).addClass('active');
      $(this).siblings('.mobile-view_icon').removeClass('active');
  
      if($(this).hasClass('single')){
        $('#product-grid').addClass('single');
      }else{
        $('#product-grid').removeClass('single');
      }
    });
   if($(window).width() < 749)
    {
      $('.coll-filter-right .filter__icon').on('click', function(){
        $('.mobile-facets__open-wrapper').trigger('click');
      });
    }
  
    $('facet-filters-form .facets__summary').on('click',function(){
      // $(this).parents('details-accordion').siblings('details-accordion').find('details[open] summary.facets__summary[aria-expanded="true"]').trigger('click');
      // $(this).parents('details-accordion').siblings('details-accordion').find('details[open] summary.facets__summary').addClass('click');
      // $(this).parents('details-accordion').siblings('details-accordion').find('details[open]').find('summary.facets__summary').trigger('click');
    });
  
    $(document).on('click', function(e){
      if(!e.target.classList.contains("facets__summary")){
        $('details-accordion').find('details[open] summary.facets__summary[aria-expanded="true"]').trigger('click');
      }
    });
  
  if(!$('body').hasClass('index')){
    var width = $(window).width();
    //console.log(width);
    if(width > 749){
     $('.carousel').flickity({
        // options
        fullscreen: true,
        lazyLoad: 1,
        pageDots: false,
        draggable: false,
        wrapAround: true
      });
    }else{
       $('.carousel').flickity({
        // options
        fullscreen: true,
        lazyLoad: 1,
        pageDots: false,
        draggable: true,
        wrapAround: true
      });
    }
  }else{
    var isALARendered;
    function ALAcheck() {
      isALARendered = setInterval(function () {
        customALA()
      }, 200);
    }
    ALAcheck();
    function customALA() {
      var selector = $('.slider-mobile-gutter .product-grid.slick-initialized');
      if(selector.length > 0) {
        clearInterval(isALARendered);
          var width = $(window).width();
        //console.log(width);
        if(width > 749){
         $('.carousel').flickity({
            // options
            fullscreen: true,
            lazyLoad: 1,
            pageDots: false,
            draggable: false,
            wrapAround: true
          });
        }else{
           $('.carousel').flickity({
            // options
            fullscreen: true,
            lazyLoad: 1,
            pageDots: false,
            draggable: true,
            wrapAround: true
          });
        }
      }  
    }
  }
  
    $(window).resize(function(){
      var width = $(window).width();
         if(width > 749){
       $('.carousel').flickity({
          // options
          fullscreen: true,
          lazyLoad: 1,
          pageDots: false,
          draggable: false,
          wrapAround: true
        });
      }else{
         $('.carousel').flickity({
          // options
          fullscreen: true,
          lazyLoad: 1,
          pageDots: false,
          draggable: true,
          wrapAround: true
        });
      }
    });
  
      var scrollTrigger= $("*[scroll-trigger]");
      scrollTrigger.bind('click', function(e) {
          e.preventDefault(); // prevent hard jump, the default behavior
          var _this = $(this); 
          var triggerAttr = _this.attr('scroll-trigger'); 
          var scrollTarget = $('*[scroll-target='+triggerAttr+']');  // Set the target as variable
  
          // perform animated scrolling by getting top-position of target-element and set it as scroll target
      $('html, body').stop().animate({
          scrollTop: scrollTarget.offset().top - 0
      }, 600 );
          return false;
      });   
  });